<script lang="ts">
import { defineComponent } from 'vue';
import { useFixturesStore } from '@/stores/fixtures';
import { mapActions, mapState } from 'pinia';
import { formatDate } from '@/utils/format';
import { getIconAlias } from '@/utils/transform';
import IconSports from '@/helpers/icon-pack-sports.vue';
import ClipboardIcon from '@/components/clipboard-icon.vue';
import { useAppStore } from '@/stores/app';

export default defineComponent({
  name: 'in-progress-fixtures',
  components: { ClipboardIcon, IconSports },
  computed: {
    ...mapState(useFixturesStore, ['inProgressFixturesLoading']),
  },
  methods: {
    ...mapActions(useAppStore, ['canWriteSection']),
    formatDate,
    getIconAlias,
  },
  props: ['fixtures', 'finishEvent', 'refresh', 'offset'],
});
</script>

<template>
  <template v-if="!fixtures && inProgressFixturesLoading">
    <div class="absolute left-80 right-0 top-0 bottom-0 grid items-center text-center">
      <div>
        <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
      </div>
    </div>
  </template>
  <template v-else-if="!fixtures">
    <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
  </template>
  <template v-else>
    <section class="relative mx-3">
      <div v-if="inProgressFixturesLoading" class="table-loader">
        <i class="pi pi-spinner animate-spin text-gray-200" style="font-size: 5em" />
      </div>
      <table class="basic-table__unaligned" style="width: 100%">
        <thead>
          <tr>
            <th style="min-width: 40px; width: 40px">
              <span class="text-md">&#8470;</span>
            </th>
            <th style="min-width: 80px; width: 80px">
              <span class="text-md">ID</span>
            </th>
            <th style="width: 1px"></th>
            <th style="text-align: left">
              <span class="text-lg">Турнир</span>
            </th>
            <th style="text-align: left">
              <span class="text-lg">Матч</span>
            </th>
            <th style="width: 1px" class="text-left">
              <span class="text-lg mx-1">Спорт</span>
            </th>
            <th style="width: 1px" class="text-center" title="Результаты по маркетам">
              <span class="text-md">RS</span>
            </th>
            <th style="width: 1px" class="text-center" title="Незакрытые маркеты">
              <span class="text-md">OM</span>
            </th>
            <th style="width: 1px" class="text-center" title="Ставок">
              <span class="text-md">BC</span>
            </th>
            <th style="width: 1px" class="text-center" title="Отказов">
              <span class="text-md">RC</span>
            </th>
            <th style="width: 1px" class="text-left">
              <span class="text-lg">Начало</span>
            </th>
            <th style="width: 80px">
              <span class="cursor-pointer mx-2" @click="refresh">
                <i class="pi pi-refresh" style="font-size: 1.7em" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(fixture, i) in fixtures || []" class="row__hoverable">
            <td class="text-center">{{ offset + i + 1 }}.</td>
            <td class="text-nowrap">
              <router-link
                :to="{ name: 'detailed-event', params: { id: fixture.id } }"
                class="text-blue-500 underline text-sm"
              >
                {{ fixture.id }}
              </router-link>
              <clipboard-icon :text="fixture.id" class="ml-1 text-sm" />
            </td>
            <td>
              <IconSports
                cssClass="fill-gray-600 dark:fill-white mt-1"
                :id="getIconAlias(fixture.sport_id)"
                :height="20"
                :width="20"
                :title="fixture.sport_name"
              />
            </td>
            <td style="text-align: left">
              <span class="text-lg">{{ fixture.competition_name }}</span>
              <clipboard-icon :text="fixture.competition_name" class="ml-1 text-sm" />
            </td>
            <td style="text-align: left">
              <span class="text-lg">{{ fixture.name }}</span>
              <clipboard-icon :text="fixture.name" class="ml-1 text-sm" />
            </td>
            <td>
              <div class="flex">
                <span class="text-lg mx-1 text-nowrap">{{ fixture.sport_name }}</span>
              </div>
            </td>
            <td class="text-center" title="Количество результатов по всем маркетам">
              <span class="text-lg">{{ fixture.results_count }}</span>
            </td>
            <td class="text-center" title="Незакрытые маркеты (кэфы не все рассчитаны)">
              <span class="text-lg">{{ fixture.open_bets_count }}</span>
            </td>
            <td class="text-center" title="Принято ставок">
              <span :class="{
                'text-lg': true,
                'text-green-500 opacity-70': +fixture.bets_count > 0
              }">{{ fixture.bets_count }}</span>
            </td>
            <td class="text-center" title="Отказано ставок">
              <span :class="{
                'text-lg': true,
                'text-yellow-500 opacity-70': +fixture.rejected_count > 0
              }">{{ fixture.rejected_count }}</span>
            </td>
            <td>
              <span class="text-lg">
                {{ formatDate(fixture.start_time_utc, 'DD.MM.YYYY&nbsp;&nbsp;HH:mm') }}
              </span>
            </td>
            <td class="text-center">
              <span class="cursor-pointer" @click="finishEvent(fixture)" v-if="canWriteSection('inprogress')">
                <i
                  class="pi pi-fast-forward text-red-500"
                  style="font-size: 1.7em"
                  title="Завершить событие"
                />
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </template>
</template>

<style scoped lang="scss">
.row__hoverable {
  @apply text-gray-500;
  &:hover {
    @apply text-gray-800 dark:text-gray-200 bg-gray-200 dark:bg-gray-800;
  }
}
</style>
